// ============================================================
// 异步任务类型注册中心
// 因为轮询状态要落 localStorage（不能存函数/闭包），所以把"如何渲染通知"
// 拆成：可序列化的 AsyncTaskContext（存 localStorage）+ 预先注册的纯函数（存代码里）。
// 各端需在 App 启动时就 import 注册文件（不能放在懒加载的路由组件里），
// 保证刷新页面后 restore() 时也能找到对应 handler。
//
// 展示名优先用后端 batchDetail.taskTypeLabel（队列会缓存最后一次）；
// handler 只负责「这类任务」的行为模板（去业务页 / 去下载中心），不必为每个业务页重复注册。
// ============================================================
import type { RouteLocationRaw } from "vue-router";
import type { AsyncTaskContext, AsyncTaskDetail } from "@shared/types/asyncTask";
import { t } from "@shared/utils/i18n";

/** 通知正文里的一个可点击片段：路由跳转 或 直接下载 */
export interface AsyncTaskNotifyAction {
    text: string;
    to?: RouteLocationRaw;
    /** 下载直链（如导出文件 url）；与 to 同时存在时优先走 download */
    download?: string;
}

/** 通知内容：标题 + 正文片段（纯文本与可点击片段混排，还原"文字里穿插链接"的排版） */
export interface AsyncTaskNotifyContent {
    title: string;
    segments: Array<string | AsyncTaskNotifyAction>;
}

export interface AsyncTaskHandler {
    /**
     * 前端兜底展示名（仅队列还从未拿到 batchDetail 时用）。
     * 正常成功/失败/放弃优先用后端 taskTypeLabel。
     */
    taskTypeLabel?: string;
    /** 提交后的一次性 toast 文案（纯文本） */
    buildPendingToast(ctx: AsyncTaskContext): string;
    /** 成功：右侧常驻通知内容（展示名用 task.taskTypeLabel） */
    buildSuccess(task: AsyncTaskDetail, ctx: AsyncTaskContext): AsyncTaskNotifyContent;
    /** 后端明确返回失败：右侧常驻通知内容 */
    buildFailed(task: AsyncTaskDetail, ctx: AsyncTaskContext): AsyncTaskNotifyContent;
    /**
     * 前端 2h 放弃时的失败通知（可选）。
     * @param taskTypeLabel 队列里缓存的最近一次后端展示名
     */
    buildGiveUp?(ctx: AsyncTaskContext, taskTypeLabel?: string): AsyncTaskNotifyContent;
}

/** 导出类任务失败/成功通知：跳转下载中心（path 由各端传入） */
export function createDownloadCenterAction(path: string, text = t("去下载中心查看")): AsyncTaskNotifyAction {
    return { text, to: { path } };
}

export interface AsyncTaskPollingConfig {
    /** active 快轮询窗口，超过仍未完成则转入 waiting；默认 5 分钟 */
    activeWindowMs?: number;
    /** waiting 阶段的抽查间隔；默认 10 分钟 */
    waitCheckIntervalMs?: number;
    /** waiting 阶段最长保留时间，超过则放弃；默认 2 小时 */
    maxWaitMs?: number;
    /**
     * 2 小时放弃时是否要弹失败提示框；默认 false。
     * 同步类建议 true（配合 buildGiveUp）；导出类也可 true，并带「去下载中心查看」。
     */
    notifyOnGiveUp?: boolean;
}

const DEFAULT_POLLING_CONFIG: Required<AsyncTaskPollingConfig> = {
    activeWindowMs: 5 * 60 * 1000,
    waitCheckIntervalMs: 10 * 60 * 1000,
    maxWaitMs: 2 * 60 * 60 * 1000,
    notifyOnGiveUp: false,
};

const handlerRegistry = new Map<string, AsyncTaskHandler>();
const pollingConfigRegistry = new Map<string, AsyncTaskPollingConfig>();

export function registerAsyncTaskHandler(taskType: string, handler: AsyncTaskHandler) {
    handlerRegistry.set(taskType, handler);
}

export function getAsyncTaskHandler(taskType: string): AsyncTaskHandler | undefined {
    return handlerRegistry.get(taskType);
}

export function registerAsyncTaskPollingConfig(taskType: string, config: AsyncTaskPollingConfig) {
    pollingConfigRegistry.set(taskType, config);
}

export function getAsyncTaskPollingConfig(taskType: string): Required<AsyncTaskPollingConfig> {
    return { ...DEFAULT_POLLING_CONFIG, ...pollingConfigRegistry.get(taskType) };
}
